import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ClientSuccessStories = () => {
  const [activeStory, setActiveStory] = useState(0);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const successStories = [
    {
      id: 'mjclothing',
      clientName: 'MJ Clothing',
      industry: 'E-Commerce & Fashion Retail',
      projectType: 'Full-Stack E-Commerce Platform',
      timeline: '4 weeks',
      liveUrl: 'https://www.mjclothing.co.in/',
      image: '/assets/mjclothing-hero.png',
      clientLogo: 'https://www.mjclothing.co.in/logo.jpg',
      testimonial: `Byte Pairs engineered our modern e-commerce web platform for MJ Clothing. The responsive web design, smooth product catalog, and seamless ordering experience elevated our brand presence and boosted customer sales.`,
      clientRole: 'MJ Clothing Management',
      beforeMetrics: {
        loadTime: '3.8s',
        mobileScore: '45%',
        onlineReach: 'Limited',
        orderFlow: 'Manual'
      },
      afterMetrics: {
        loadTime: '0.8s',
        mobileScore: '98%',
        onlineReach: 'Pan-India',
        orderFlow: 'Automated'
      },
      keyFeatures: [
        'Mobile-First Responsive E-Commerce UI',
        'Product Catalog & Category Filtering',
        'High Performance Page Load Speed',
        'Custom Brand Identity & Order Integration'
      ],
      techStack: ['React', 'Node.js', 'Tailwind CSS', 'Vite', 'REST API'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'ddservice',
      clientName: 'DD Service',
      industry: 'Ashok Leyland & LCV Mechanic Service',
      projectType: 'Vehicle Repair Booking & Breakdown Web App',
      timeline: '3 weeks',
      liveUrl: 'https://ddservice.netlify.app/',
      image: '/assets/ddservice-hero.png',
      clientLogo: 'https://ddservice.netlify.app/assets/logo-MDUGI9Kf.png',
      testimonial: `Byte Pairs engineered our digital service booking and emergency repair portal for DD Service. Vehicle owners and fleet drivers across Tamil Nadu can easily schedule Ashok Leyland light commercial vehicle maintenance, request emergency repair visits, and contact expert mechanics instantly.`,
      clientRole: 'DD Service Team',
      beforeMetrics: {
        bookingTime: '20 mins',
        onlinePresence: '0%',
        mobileTraffic: '25%',
        serviceRange: 'Local'
      },
      afterMetrics: {
        bookingTime: '< 1 min',
        onlinePresence: '100%',
        mobileTraffic: '88%',
        serviceRange: 'Statewide'
      },
      keyFeatures: [
        'Ashok Leyland & LCV Service Request System',
        'Emergency Breakdown & Repair Booking',
        'Direct Call & WhatsApp Mechanic Support',
        'Mobile-First Fast Loading Web Application'
      ],
      techStack: ['React', 'Vite', 'Tailwind CSS', 'Netlify', 'SEO'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'dileo',
      clientName: 'Vellankanni Stores',
      industry: 'Supermarket & Grocery Retail',
      projectType: 'Fresh Grocery & Organic Produce E-Commerce Web App',
      timeline: '3 weeks',
      liveUrl: 'https://www.dileo.co.in/',
      image: '/assets/vellankanni-stores-hero.png',
      clientLogo: 'https://www.dileo.co.in/favicon.png',
      testimonial: `Byte Pairs engineered our modern online grocery web store for Vellankanni Stores (Dharapuram). Our customers can easily browse fresh produce, organic groceries, and daily home essentials with fast digital ordering and local delivery.`,
      clientRole: 'Vellankanni Stores Team',
      beforeMetrics: {
        orderingFlow: 'In-Store Only',
        onlinePresence: '0%',
        catalogAccess: 'Manual',
        deliverySetup: 'Phone Calls'
      },
      afterMetrics: {
        orderingFlow: 'Online E-Commerce',
        onlinePresence: '100% Dharapuram',
        catalogAccess: 'Digital Catalog',
        deliverySetup: 'Automated Doorstep'
      },
      keyFeatures: [
        'Fresh Groceries & Farm Produce Product Catalog',
        'Mobile-First Responsive Shopping Experience',
        'Local Search & SEO for Dharapuram Region',
        'Fast Page Speed & Smooth Navigation UI'
      ],
      techStack: ['React', 'Vite', 'Tailwind CSS', 'FontAwesome', 'SEO'],
      color: 'from-emerald-500 to-teal-500'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const currentStory = successStories[activeStory];

  return (
    <section ref={sectionRef} id="client-projects" className="py-20 bg-gradient-to-b from-muted/30 to-background">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-primary/20">
            <Icon name="Briefcase" size={16} />
            <span>Our Client Projects</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Client <span className="text-gradient">Success Showcase</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Explore live digital products engineered by Byte Pairs. We partner with businesses to build high-performance web apps and e-commerce solutions.
          </p>
        </motion.div>

        {/* Story Navigation */}
        <motion.div
          className="flex flex-wrap justify-center gap-4 mb-12"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {successStories.map((story, index) => (
            <motion.button
              key={story.id}
              variants={cardVariants}
              onClick={() => setActiveStory(index)}
              className={`px-6 py-4 rounded-2xl border transition-all duration-300 ${activeStory === index
                ? 'bg-card text-foreground border-primary shadow-brand ring-2 ring-primary/20'
                : 'bg-card/60 text-text-secondary border-border hover:border-primary/50 hover:text-foreground'
                }`}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-white p-1 flex items-center justify-center border border-border">
                  <Image
                    src={story.clientLogo}
                    alt={`${story.clientName} logo`}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="text-left">
                  <div className="font-bold text-foreground">{story.clientName}</div>
                  <div className="text-xs text-text-secondary">{story.industry}</div>
                </div>
              </div>
            </motion.button>
          ))}
        </motion.div>

        {/* Active Story Display */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            {/* Story Content */}
            <div className="space-y-8">
              {/* Client Info & Live Link */}
              <div className="flex items-center justify-between flex-wrap gap-4 bg-card border border-border p-4 rounded-2xl">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-xl bg-white p-2 flex items-center justify-center border border-border shadow-sm">
                    <Image
                      src={currentStory.clientLogo}
                      alt={`${currentStory.clientName} logo`}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">{currentStory.clientName}</h3>
                    <p className="text-text-secondary text-sm">{currentStory.industry} • {currentStory.timeline}</p>
                  </div>
                </div>

                <a
                  href={currentStory.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-primary text-white px-4 py-2.5 rounded-xl font-medium text-sm hover:bg-primary/90 transition-all duration-300 shadow-sm"
                >
                  <span>Visit Website</span>
                  <Icon name="ExternalLink" size={16} />
                </a>
              </div>

              {/* Testimonial */}
              <blockquote className="relative bg-card/40 border border-border/60 p-6 rounded-2xl">
                <div className="absolute -top-4 -left-2 text-5xl text-primary/20 font-serif">"</div>
                <p className="text-lg text-text-secondary leading-relaxed italic pl-4">
                  {currentStory.testimonial}
                </p>
                <footer className="mt-4 pl-4">
                  <cite className="text-foreground font-semibold not-italic">
                    — {currentStory.clientRole}
                  </cite>
                </footer>
              </blockquote>

              {/* Key Features */}
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-4">Key Features Delivered:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentStory.keyFeatures.map((feature, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center space-x-3 bg-card/60 p-2.5 rounded-xl border border-border/50"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="w-5 h-5 bg-success rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={12} color="white" />
                      </div>
                      <span className="text-foreground text-sm font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Tech Stack */}
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-3">Technology Stack:</h4>
                <div className="flex flex-wrap gap-2">
                  {currentStory.techStack.map((tech, index) => (
                    <span
                      key={index}
                      className="px-3.5 py-1.5 bg-card text-foreground font-medium text-sm rounded-lg border border-border"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Visual Content */}
            <div className="space-y-8">
              {/* Project Preview */}
              <motion.div
                className="relative rounded-2xl overflow-hidden shadow-xl border border-border bg-white"
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.3 }}
              >
                <Image
                  src={currentStory.image}
                  alt={`${currentStory.clientName} project`}
                  className="w-full h-72 object-cover object-center bg-white"
                />
                
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-4 rounded-xl border border-slate-200 shadow-lg flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Project</div>
                    <div className="font-bold text-slate-900 text-sm md:text-base">{currentStory.projectType}</div>
                  </div>
                  <a
                    href={currentStory.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs md:text-sm font-bold text-primary hover:text-primary/80 transition-colors flex items-center space-x-1 bg-primary/10 px-3 py-1.5 rounded-lg border border-primary/20"
                  >
                    <span>{currentStory.liveUrl.replace('https://', '')}</span>
                    <Icon name="ArrowUpRight" size={14} />
                  </a>
                </div>
              </motion.div>

              {/* Before/After Metrics */}
              <div className="bg-card border border-border rounded-2xl p-6 shadow-brand">
                <h4 className="text-lg font-semibold text-foreground mb-6 text-center flex items-center justify-center space-x-2">
                  <Icon name="TrendingUp" size={20} className="text-success" />
                  <span>Performance Impact</span>
                </h4>

                <div className="grid grid-cols-2 gap-6">
                  {/* Before */}
                  <div className="bg-muted/40 p-4 rounded-xl border border-border/50">
                    <h5 className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-3 text-center">Before Byte Pairs</h5>
                    <div className="space-y-2.5">
                      {Object.entries(currentStory.beforeMetrics).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center text-xs">
                          <span className="text-text-secondary capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                          <span className="font-semibold text-error">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* After */}
                  <div className="bg-primary/5 p-4 rounded-xl border border-primary/20">
                    <h5 className="text-xs font-bold text-primary uppercase tracking-wider mb-3 text-center">With Byte Pairs</h5>
                    <div className="space-y-2.5">
                      {Object.entries(currentStory.afterMetrics).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center text-xs">
                          <span className="text-text-secondary capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                          <span className="font-bold text-success">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Overall Stats */}
        <motion.div
          className="mt-16 bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 rounded-2xl p-8 border border-primary/20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            Measured Client Satisfaction
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-primary mb-1">100%</div>
              <div className="text-text-secondary text-sm font-medium">On-Time Delivery</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-success mb-1">99.9%</div>
              <div className="text-text-secondary text-sm font-medium">Platform Uptime</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent mb-1">&lt; 1.0s</div>
              <div className="text-text-secondary text-sm font-medium">Average Load Speed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-warning mb-1">100%</div>
              <div className="text-text-secondary text-sm font-medium">Code Ownership</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ClientSuccessStories;